// TODO move to regular components
import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { get } from "lodash";
import { uniqueId } from "lodash";

import { StyledHorizontalScrollDiv, StyledTable } from "../../../useGeneralStyles";
import { ContainerWithLoader, PaginationWithLimit, TableDataCellWithSort } from "../index";
import copyToClipboardInXlsFormat, {
  buildTableRows,
  IColum,
} from "../../../utils/copyToClipboardInXlsFormat";

import { IPaginationState } from "../../../hooks/usePagination";
import { ISortBy } from "../../../types";
import downloadCsv from "../../../utils/downloadCsv";

type IProps<D> = {
  columns: IColum[];
  subColumns?: IColum[];
  title?: React.ReactElement | null;
  loading: boolean;
  data: (D & { sub_table_data?: { id: string; [key: string]: string | number }[] })[];
  extractKey: (data: D, index: number) => string | number;
  colSpan?: number;
  theadTitle?: string;
  csv_table_name?: string;
  isDownloadable?: boolean;
  noResultsMessage?: string;
  pagination?: IPaginationState;
} & (
  | {
      handleSortBy?: (arg0: string) => void;
      sortBy?: ISortBy;
    }
  | {
      handleSortBy: (arg0: string) => void;
      sortBy: ISortBy;
    }
);

const Table = <D,>({
  title,
  loading,
  data,
  columns,
  pagination,
  extractKey,
  subColumns,
  colSpan,
  theadTitle,
  csv_table_name = "",
  sortBy,
  handleSortBy,
  isDownloadable = false,
  noResultsMessage = "There are no results",
}: IProps<D>): React.ReactElement => {
  const [tooltipText, setTooltipText] = React.useState("Copy to clipboard");

  const handleDownload = () => {
    const rows = buildTableRows({
      data,
      columns,
      sub_columns: subColumns,
      table_name: csv_table_name,
      isForCSV: true,
    });

    downloadCsv(rows as string[][], csv_table_name);
  };

  const handleCopyToClipBoard = () => {
    copyToClipboardInXlsFormat({
      data,
      columns,
      sub_columns: subColumns,
      table_name: csv_table_name,
    });
    setTooltipText("Copied");
  };
  const handleTooltipClose = () => {
    setTimeout(() => {
      setTooltipText("Copy to clipboard");
    }, 500);
  };

  const handleChangePage = (page: number) => pagination && pagination.setPage(page);

  return (
    <Box mt="15px">
      {title && (
        <div>
          <span>{title}</span>{" "}
        </div>
      )}

      <ContainerWithLoader isLoading={loading} report={data} noResultsMessage={noResultsMessage}>
        <StyledHorizontalScrollDiv>
          <StyledTable smTable tableWithoutHover noTextWrap>
            <thead>
              <tr>
                <td colSpan={colSpan || columns.length}>
                  <Box display="flex" alignItems="center">
                    <Tooltip title={tooltipText} placement="top">
                      <IconButton
                        size="small"
                        onClick={handleCopyToClipBoard}
                        onMouseLeave={handleTooltipClose}
                      >
                        <FileCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                    {isDownloadable && (
                      <Tooltip placement="top" title="Download">
                        <IconButton
                          size="small"
                          onClick={handleDownload}
                          onMouseLeave={handleTooltipClose}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {theadTitle && (
                      <Typography fontWeight={800} color="#0e0c0c">
                        {theadTitle}
                      </Typography>
                    )}
                  </Box>
                </td>
              </tr>
              <tr>
                {columns.map((r) => {
                  if (r.isSortable && sortBy && handleSortBy) {
                    return (
                      <TableDataCellWithSort
                        key={r.by}
                        by={r.by}
                        sortBy={sortBy}
                        newSortBy={handleSortBy}
                        title={r.title}
                        titleComponent={r.titleComponent}
                      />
                    );
                  }

                  if (r.titleComponent) return <td key={r.by}>{r.titleComponent}</td>;
                  return <td key={r.by}>{r.title}</td>;
                })}
              </tr>
            </thead>

            <tbody>
              {data.map((d, index) => {
                const { sub_table_data } = d;
                return (
                  <React.Fragment key={extractKey(d, index)}>
                    <Box
                      component="tr"
                      sx={{
                        ...(sub_table_data &&
                          !sub_table_data.length && {
                            "& td": {
                              borderBottom: "1px solid #000",
                            },
                          }),
                      }}
                    >
                      {columns.map((r, i) => {
                        const cellValue = r.getCellValue
                          ? r.getCellValue(get(d, r.path, ""), d)
                          : get(d, r.path, "");
                        const key =
                          typeof cellValue === "object" || !cellValue ? i : cellValue + uniqueId();
                        return <td key={key}>{cellValue as React.ReactNode}</td>;
                      })}
                    </Box>
                    {!!sub_table_data?.length && (
                      <>
                        {
                          <Box
                            component="tr"
                            sx={{ "& td": { backgroundColor: "#d7e3fc !important" } }}
                          >
                            {subColumns &&
                              subColumns.map((sc) => <td key={sc.title}>{sc.title}</td>)}
                          </Box>
                        }
                        {sub_table_data.map((subData) => {
                          const isLastRow = sub_table_data.at(-1)?.id === subData.id;
                          return (
                            <Box
                              component="tr"
                              key={subData.id}
                              sx={{
                                ...(isLastRow && { "& td": { borderBottom: "1px solid #000" } }),
                              }}
                            >
                              {subColumns &&
                                subColumns.map(({ path, getCellValue }) => {
                                  return (
                                    <td key={path}>
                                      {getCellValue
                                        ? getCellValue(subData[path], subData)
                                        : subData[path]}
                                    </td>
                                  );
                                })}
                            </Box>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </StyledTable>
        </StyledHorizontalScrollDiv>
      </ContainerWithLoader>
      {pagination && data.length > 0 && (
        <PaginationWithLimit {...pagination} setPageState={handleChangePage} />
      )}
    </Box>
  );
};

export default Table;
